import React from 'react';
import {
  Button,
  Container,
  Form,
  Modal,
  ProgressBar,
  Alert,
  Spinner,
  ListGroup,
  Tabs,
  Tab,
} from 'react-bootstrap';
import {GlobalWorkerOptions, getDocument} from 'pdfjs-dist';
import type {TextContent, TextItem} from 'pdfjs-dist/types/src/display/api';
import * as Tesseract from 'tesseract.js';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {FaLinkedin} from 'react-icons/fa';
import {SiIndeed} from 'react-icons/si';
import {track} from '../libs/analytics';
import {useAuth} from '../auth/auth-context';
import ResumeForm, {validateResumePayload} from '../models/resume-form';
import type {ResumeDbModel} from '../models/database-models';
import {useAppDispatch} from '../store/hooks';
import {setHasTailored, setHasResume} from '../store/quickstart-slice';
import {getAxiosErrorMessage} from '../libs/helper';

// Use a CDN worker for pdf.js to avoid bundling issues in CRA setups.
// This points to a commonly available pdf.js worker; adjust if you host your own.
GlobalWorkerOptions.workerSrc =
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.7.284/build/pdf.worker.min.mjs';

type TextContentItem = TextContent['items'][number];

function isTextItem(item: TextContentItem): item is TextItem {
  return 'str' in item;
}

type ProgressState = {
  page: number;
  totalPages: number;
  pageProgress: number; // 0..1 for current page (render or ocr)
  overallProgress: number; // 0..1 across document
  status: string;
};

type ImportStep =
  | 'idle'
  | 'extract'
  | 'ocr'
  | 'review'
  | 'ai'
  | 'save'
  | 'done';

export default function ImportPdfPage(): React.JSX.Element {
  const [loading, setLoading] = React.useState(false);
  const [progress, setProgress] = React.useState<ProgressState | undefined>(
    undefined,
  );
  const [, setText] = React.useState<string>('');
  const [error, setError] = React.useState<string | undefined>(undefined);
  const cancelledRef = React.useRef(false);
  const tesseractWorkerRef = React.useRef<Tesseract.Worker | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  const {refreshAuthLoading} = useAuth();
  const dispatch = useAppDispatch();

  // Minimal UI state
  const [dragActive, setDragActive] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | undefined>(
    undefined,
  );
  const [pageCount, setPageCount] = React.useState<number | undefined>(
    undefined,
  );
  const [ocrPages, setOcrPages] = React.useState<number>(0);
  const [, setStep] = React.useState<ImportStep>('idle');
  const [result, setResult] = React.useState<ResumeDbModel | undefined>(
    undefined,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const busy = loading || refreshAuthLoading;

  // AI cancel requested flag
  const aiCancelRequestedRef = React.useRef<boolean>(false);

  // Track which language the worker is initialized with
  const currentOcrLangRef = React.useRef<string>('eng');

  React.useEffect(
    () => () => {
      // Cleanup on unmount
      cancelledRef.current = true;
      if (tesseractWorkerRef.current) {
        void tesseractWorkerRef.current.terminate();
        tesseractWorkerRef.current = undefined;
      }
    },
    [],
  );

  async function ensureTesseractWorker(lang = 'eng') {
    const targetLang = lang;
    if (!tesseractWorkerRef.current) {
      const worker = await Tesseract.createWorker(targetLang);
      tesseractWorkerRef.current = worker;
      currentOcrLangRef.current = targetLang;
      return worker;
    }

    // Re-initialize if language changed
    const worker = tesseractWorkerRef.current;
    if (!worker) {
      return Tesseract.createWorker(targetLang);
    }

    if (currentOcrLangRef.current !== targetLang) {
      await worker.reinitialize(targetLang);
      currentOcrLangRef.current = targetLang;
    }

    return worker;
  }

  async function handleFile(file?: File) {
    setError(undefined);
    setText('');
    if (!file) {
      return;
    }

    cancelledRef.current = false;
    setLoading(true);
    setSelectedFile(file);
    setStep('extract');
    // Started extraction

    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = getDocument({data: arrayBuffer});
      const pdf = await loadingTask.promise;
      const total = pdf.numPages;
      setPageCount(total);

      let accumulatedText = '';
      let ocrUsed = 0;
      const processPage = async (pageNumber: number): Promise<void> => {
        if (pageNumber > total) {
          return;
        }

        if (cancelledRef.current) {
          setProgress(p => (p ? {...p, status: 'Cancelled'} : undefined));
          return;
        }

        setProgress({
          page: pageNumber,
          totalPages: total,
          pageProgress: 0,
          overallProgress: (pageNumber - 1) / total,
          status: 'Extracting text layer',
        });

        const page = await pdf.getPage(pageNumber);

        let pageText = '';
        try {
          const textContent = await page.getTextContent();
          pageText = textContent.items
            .filter((item): item is TextItem => isTextItem(item))
            .map(item => item.str)
            .join(' ');
        } catch {
          // ignore and fall back to OCR
        }

        if (pageText.trim().length > 0) {
          accumulatedText += `\n\n--- Page ${pageNumber} ---\n\n${pageText}`;
          setProgress(p =>
            p
              ? {
                  ...p,
                  pageProgress: 1,
                  overallProgress: pageNumber / total,
                  status: 'Text extracted from layer',
                }
              : undefined,
          );
          await processPage(pageNumber + 1);
          return;
        }

        setStep('ocr');
        setProgress(p =>
          p
            ? {...p, pageProgress: 0, status: 'Rendering page for OCR'}
            : undefined,
        );

        const viewport = page.getViewport({scale: 2});
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error(
            'Unable to get canvas context for rendering PDF page.',
          );
        }

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        const renderTask = page.render({
          canvasContext: context,
          canvas,
          viewport,
        });

        await renderTask.promise;
        setProgress(p =>
          p ? {...p, pageProgress: 0.2, status: 'Running OCR'} : undefined,
        );

        const worker = await ensureTesseractWorker();
        ocrUsed++;
        const {data} = await worker.recognize(canvas);
        const ocrText = data.text;
        accumulatedText += `\n\n--- Page ${pageNumber} (OCR) ---\n\n${ocrText}`;

        setProgress({
          page: pageNumber,
          totalPages: total,
          pageProgress: 1,
          overallProgress: pageNumber / total,
          status: 'OCR complete',
        });

        await new Promise(resolve => {
          setTimeout(resolve, 50);
        });

        await processPage(pageNumber + 1);
      };

      await processPage(1);

      const finalText = accumulatedText.trim();
      setText(finalText);
      setProgress(p =>
        p ? {...p, overallProgress: 1, status: 'Completed'} : undefined,
      );
      setStep('review');
      setOcrPages(ocrUsed);

      if (cancelledRef.current) {
        // User cancelled; stop further processing
        setLoading(false);
        return;
      }

      // Generate AI resume after extracting text
      await generateAiResume(finalText);
    } catch (error_: unknown) {
      // Handle any error during PDF parsing/processing
      console.error('Import PDF error', error_);
      setError(getAxiosErrorMessage(error_) ?? String(error_));
    } finally {
      // Ensure loading is false
      setLoading(false);
    }
  }

  async function generateAiResume(finalText: string): Promise<void> {
    setProgress(p =>
      p ? {...p, status: 'Generating resume with AI'} : undefined,
    );
    setStep('ai');
    try {
      aiCancelRequestedRef.current = false;
      const aiResponse = await axios.post<ResumeDbModel>('/api/ai/resumes', {
        resume_text: finalText.normalize('NFC'), // eslint-disable-line @typescript-eslint/naming-convention
      });
      const aiModel = aiResponse.data;
      if (!aiModel) {
        throw new Error('AI service returned no resume model');
      }

      if (aiCancelRequestedRef.current) {
        setError('Operation cancelled');
        return;
      }

      setProgress(p =>
        p ? {...p, overallProgress: 1, status: 'Generated'} : undefined,
      );
      setStep('review');
      setResult(aiModel);
      track('ai_resume_created', {method: 'pdf_import', ocrPages});
      try {
        dispatch(setHasTailored(true));
      } catch {}
    } catch (error_: unknown) {
      if (aiCancelRequestedRef.current) {
        setError('Operation cancelled');
      } else {
        console.error('AI/import error', error_);
        setError(getAxiosErrorMessage(error_) ?? String(error_));
      }
    } finally {
      aiCancelRequestedRef.current = false;
    }
  }

  function handleFileInput(event: React.ChangeEvent<HTMLInputElement>) {
    const f = event.target.files?.[0] ?? undefined;
    setSelectedFile(f);
    event.currentTarget.value = '';
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    const f = event.dataTransfer.files?.[0] ?? undefined;
    setSelectedFile(f);
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleDragEnter(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }

  function handleDragLeave(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }

  function handleDiscard() {
    setError(undefined);
    setProgress(undefined);
    setText('');
    setStep('idle');
    setResult(undefined);
  }

  async function handleSave(v: ResumeDbModel) {
    const validationErrors = validateResumePayload(v);
    if (validationErrors.summary.length > 0) {
      setError(
        `Fix these resume format issues: ${validationErrors.summary.join(' ')}`,
      );
      return;
    }

    try {
      setLoading(true);
      setError(undefined);
      const response = await axios.post<ResumeDbModel>('/api/resumes', v);
      const created = response.data;
      track('resume_saved', {id: created?.id, method: 'pdf_import'});
      try {
        dispatch(setHasResume(true));
      } catch {}

      await navigate(created?.id ? `/resumes/${created.id}` : '/resumes');
    } catch (error_: unknown) {
      console.error('Save PDF import error', error_);
      setError(
        `Failed to save: ${getAxiosErrorMessage(error_) ?? 'Unknown error'}`,
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container className='my-4'>
      <div className='d-flex align-items-center mb-2'>
        <h2 className='mb-0 me-3'>Import PDF Resume</h2>
        <div className='ms-auto'>
          <Button
            variant='outline-secondary'
            onClick={() => {
              void navigate('/resumes');
            }}
          >
            Back
          </Button>
        </div>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${dragActive ? '#0d6efd' : '#bbb'}`,
          padding: '1rem',
          borderRadius: 6,
          marginBottom: '1rem',
          background: dragActive ? '#eef5ff' : '#fafafa',
        }}
      >
        <p className='mb-1'>
          Drop a PDF file here, or select one using the button.
        </p>
        <Form.Group controlId='pdfFile'>
          <Form.Control
            ref={fileInputRef}
            type='file'
            accept='application/pdf'
            onChange={handleFileInput}
            style={{display: 'none'}}
          />
          <Button
            variant='outline-primary'
            onClick={() => fileInputRef.current?.click()}
          >
            Choose PDF
          </Button>
        </Form.Group>
        {selectedFile && (
          <div className='mt-2 text-muted'>
            <small>
              Selected: {selectedFile.name} (
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              {pageCount ? ` — ${pageCount} page(s)` : ''}
            </small>
          </div>
        )}
      </div>

      {busy && (
        <div className='d-flex align-items-center mb-3'>
          <Spinner animation='border' className='me-2' />
          <span>Processing...</span>
        </div>
      )}

      {!busy && progress && (
        <div className='mb-2'>
          <div className='mb-1'>
            <strong>{progress.status}</strong> — Page {progress.page} /{' '}
            {progress.totalPages}
          </div>
          <ProgressBar
            className='mb-2'
            now={(progress.overallProgress || 0) * 100}
            label={`${Math.round((progress.overallProgress || 0) * 100)}% overall`}
          />
          <ProgressBar
            variant='info'
            now={(progress.pageProgress || 0) * 100}
            label={`${Math.round((progress.pageProgress || 0) * 100)}% page`}
          />
          <div className='text-muted mt-1'>
            <small>{pageCount ? `Total pages: ${pageCount}.` : null}</small>
          </div>
          <div aria-live='polite' className='visually-hidden'>
            {progress?.status}
          </div>
        </div>
      )}

      {error && <Alert variant='danger'>{error}</Alert>}

      {!result && (
        <div className='mb-3'>
          <Button
            variant='success'
            onClick={() => {
              void handleFile(selectedFile);
            }}
            disabled={!selectedFile || busy}
          >
            {busy ? 'Processing…' : 'Import'}
          </Button>
        </div>
      )}

      {result && (
        <>
          <Alert variant='info'>
            AI resume generated — you can edit it below before saving, or
            discard it.
          </Alert>
          <ResumeForm
            value={result}
            onChange={setResult}
            onSubmit={v => {
              void handleSave(v);
            }}
            readOnly={false}
            showActions={false}
          />
          <div className='d-flex justify-content-end mt-2'>
            <Button
              variant='outline-secondary'
              className='me-2'
              onClick={handleDiscard}
              disabled={loading}
            >
              Discard
            </Button>
            <Button
              onClick={() => {
                if (result) {
                  void handleSave(result);
                }
              }}
              disabled={loading || !result}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </>
      )}
    </Container>
  );
}

type ImportFromPlatformsModalProps = {
  show: boolean;
  onHide: () => void;
};

// Provide tabbed instructions for supported platforms (LinkedIn, Indeed).
export const ImportFromPlatformsModal: React.FC<
  ImportFromPlatformsModalProps
> = ({show, onHide}) => {
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = React.useState<string>('linkedin');

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          <span className='d-inline-flex align-items-center'>
            {activeKey === 'linkedin' ? (
              <FaLinkedin
                size={24}
                style={{color: '#0a66c2', marginRight: 8}}
              />
            ) : (
              <SiIndeed size={24} style={{color: '#2557a7', marginRight: 8}} />
            )}
            {activeKey === 'linkedin'
              ? 'Import from LinkedIn'
              : 'Import from Indeed'}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Tabs
          activeKey={activeKey}
          onSelect={k => {
            setActiveKey(String(k));
          }}
          className='mb-3'
        >
          <Tab
            eventKey='linkedin'
            title={
              <span className='d-inline-flex align-items-center'>
                <FaLinkedin style={{color: '#0a66c2', marginRight: 8}} />{' '}
                LinkedIn
              </span>
            }
          >
            <p>
              You can export your LinkedIn profile to PDF and then import that
              PDF into Self Driving Resume.
            </p>

            <ListGroup as='ol' className='list-group-numbered'>
              <ListGroup.Item as='li'>
                Open your LinkedIn profile (in a new tab):{' '}
                <a
                  href='https://www.linkedin.com'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  linkedin.com
                </a>
                .
              </ListGroup.Item>
              <ListGroup.Item as='li'>
                Click the Me / Profile icon at the top of the homepage to go to
                your profile page.
              </ListGroup.Item>
              <ListGroup.Item as='li'>
                On your profile page, locate the "More" or "Resources" menu
                (near your profile header) and choose{' '}
                <strong>Save to PDF</strong> or <strong>Save as PDF</strong>.
                This will download a PDF version of your profile.
              </ListGroup.Item>
              <ListGroup.Item as='li'>
                Save the generated PDF to your computer.
              </ListGroup.Item>
              <ListGroup.Item as='li'>
                Return here and click <strong>Open Import PDF</strong> to upload
                the downloaded PDF and generate a resume.
              </ListGroup.Item>
            </ListGroup>

            <p className='mb-0'>
              For LinkedIn's official instructions see:{' '}
              <a
                href='https://www.linkedin.com/help/linkedin/answer/a541960/save-a-profile-as-a-pdf?lang=en'
                target='_blank'
                rel='noopener noreferrer'
              >
                Save a profile as a PDF (LinkedIn Help)
              </a>
              .
            </p>
          </Tab>

          <Tab
            eventKey='indeed'
            title={
              <span className='d-inline-flex align-items-center'>
                <SiIndeed style={{color: '#2557a7', marginRight: 8}} /> Indeed
              </span>
            }
          >
            <p>
              You can download your Indeed profile or resume as a PDF and then
              import that PDF into Self Driving Resume.
            </p>

            <ListGroup as='ol' className='list-group-numbered'>
              <ListGroup.Item as='li'>
                Open your Indeed profile (in a new tab):{' '}
                <a
                  href='https://www.indeed.com'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  indeed.com
                </a>
                .
              </ListGroup.Item>
              <ListGroup.Item as='li'>
                Follow Indeed's account/profile options to find a{' '}
                <strong>Download PDF</strong> or <strong>Save as PDF</strong>{' '}
                option. This is commonly available from your profile share or
                print options.
              </ListGroup.Item>
              <ListGroup.Item as='li'>
                Save the exported PDF to your computer.
              </ListGroup.Item>
              <ListGroup.Item as='li'>
                Return here and click <strong>Open Import PDF</strong> to upload
                the downloaded PDF and generate a resume.
              </ListGroup.Item>
            </ListGroup>

            <p className='mb-0'>
              For Indeed's official instructions see:{' '}
              <a
                href='https://support.indeed.com/hc/en-us/articles/204289064-How-to-Share-Download-and-Print-a-Profile'
                target='_blank'
                rel='noopener noreferrer'
              >
                How to Share, Download and Print a Profile (Indeed Help)
              </a>
              .
            </p>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Close
        </Button>
        <Button
          variant='primary'
          onClick={() => {
            onHide();
            void navigate('/resumes/import-pdf');
          }}
        >
          Open Import PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
